'use client';
import { useRef } from 'react';
import classNames from 'classnames';
import { hasCldImage, TypeBlockPageSection } from '@fm-nx/contentful-types';
import AnimationFadeLazy from '../animation-fade-lazy/animation-fade-lazy';
import { useContentfulInspectorMode, useContentfulLiveUpdates } from '@contentful/live-preview/react';
import PageSectionImage from './page-section-image';
import PageSectionContent from './page-section-content';

type PageSectionProps = TypeBlockPageSection<'WITHOUT_UNRESOLVABLE_LINKS'> & {
    isFirstBlock?: boolean;
};

export const PageSection = (props: PageSectionProps) => {
    const { fields, isFirstBlock } = props;
    const updatedData = useContentfulLiveUpdates(props);
    const wrapRef = useRef<HTMLDivElement>(null);

    const inspectorProps = useContentfulInspectorMode({ entryId: updatedData?.sys?.id });

    if (!fields) return null;

    const { imagePlacement } = fields;
    const imageCloudinary = hasCldImage(fields.image?.fields) ? fields.image?.fields.cloudinaryMedia[0] : null;

    return (
        <section className="relative w-full mx-auto max-w-container" ref={wrapRef}>
            <AnimationFadeLazy>
                <div className={classNames('my-6 md:my-20', { 'md:mt-0': isFirstBlock })}>
                    <div className="flex flex-col md:grid md:grid-cols-14 md:gap-4 md:items-center">
                        {imagePlacement === 'Left' && (
                            <>
                                {imageCloudinary && (
                                    <div className="order-2 col-span-5 col-start-2 mt-8 md:mt-0 md:order-1">
                                        <PageSectionImage
                                            {...inspectorProps({
                                                fieldId: 'image',
                                            })}
                                            url={imageCloudinary.secure_url}
                                            cloudinaryId={imageCloudinary.public_id}
                                            alt={updatedData?.fields.image?.fields?.altText}
                                            imagePlacement={imagePlacement}
                                        />
                                    </div>
                                )}
                                <div className="order-1 col-span-6 col-start-8 md:order-2">
                                    <div className="flex items-center h-full mt-6 md:mt-0">
                                        <PageSectionContent {...props} />
                                    </div>
                                </div>
                            </>
                        )}
                        {imagePlacement === 'Right' && (
                            <>
                                <div className="flex items-center col-span-6 col-start-2 my-6 md:my-16">
                                    <PageSectionContent {...props} />
                                </div>
                                {imageCloudinary && (
                                    <div className="col-span-7 col-start-9">
                                        <div className="h-full mt-6 md:mt-0">
                                            <PageSectionImage
                                                {...inspectorProps({
                                                    fieldId: 'image',
                                                })}
                                                url={imageCloudinary.secure_url}
                                                cloudinaryId={imageCloudinary.public_id}
                                                alt={updatedData?.fields.image?.fields?.altText}
                                                imagePlacement={imagePlacement}
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </AnimationFadeLazy>
        </section>
    );
};

export default PageSection;
